"use client";

import React, { useRef, useState } from "react";
import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { useLocale } from "next-intl";
import {
  MdArrowForward,
  MdOutlineChevronRight,
  MdOutlineChevronLeft,
} from "react-icons/md";

import styles from "./market-insights.module.css";

const INSIGHTS_DATA = [
  {
    id: 1,
    title: "Global Real Estate Outlook 2024",
    date: "May 15, 2024",
    badge: "Market Report",
    image: "/placeholder-images/city-living.png",
    link: "/insights/1",
  },
  {
    id: 2,
    title: "Top 10 Cities for Property Investment",
    date: "May 10, 2024",
    badge: "Investment Guide",
    image: "/placeholder-images/coastal-living.png",
    link: "/insights/2",
  },
  {
    id: 3,
    title: "Luxury Market: Trends Q2 2024",
    date: "May 5, 2024",
    badge: "Market Trends",
    image: "/placeholder-images/investment.png",
    link: "/insights/3",
  },
  {
    id: 4,
    title: "Why Countryside Properties Are In Demand",
    date: "April 28, 2024",
    badge: "Lifestyle Guide",
    image: "/placeholder-images/countryside-living.png",
    link: "/insights/4",
  },
  {
    id: 5,
    title: "International Buyers Guide to Spain",
    date: "April 20, 2024",
    badge: "Buyers Guide",
    image: "/placeholder-images/coastal-living.png",
    link: "/insights/5",
  },
];

export default function MarketInsights() {
  const locale = useLocale();
  const isRTL = locale === "ar" || locale === "he";

  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [hasDragged, setHasDragged] = useState(false);

  const scrollAmount = isRTL ? -310 : 310;

  const handleScrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -scrollAmount, behavior: "smooth" });
    }
  };

  const handleScrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setHasDragged(false);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseLeave = () => setIsDragging(false);

  const handleMouseUp = () => {
    setIsDragging(false);
    setTimeout(() => setHasDragged(false), 50);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();

    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;

    if (Math.abs(walk) > 5) {
      setHasDragged(true);
    }
  };

  return (
    <div className={styles.outerBackground}>
      <section className={styles.section}>
        <div className={styles.header}>
          <div className={styles.titleWrapper}>
            <h2 className={styles.title}>Global Market Insights</h2>
            <p className={styles.subtitle}>
              Stay informed with the latest real estate trends and reports.
            </p>
          </div>
          <Link href="/insights" className={styles.viewAllLink}>
            View all insights
            <MdArrowForward className={styles.linkIcon} />
          </Link>
        </div>

        <div className={styles.sliderWrapper}>
          <div
            className={`${styles.cardsTrack} ${isDragging ? styles.dragging : ""}`}
            ref={scrollRef}
            dir={isRTL ? "rtl" : "ltr"}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
          >
            {INSIGHTS_DATA.map((item) => (
              <div
                key={item.id}
                className={styles.cardWrapper}
                onClickCapture={(e) => {
                  if (hasDragged) {
                    e.stopPropagation();
                    e.preventDefault();
                  }
                }}
              >
                <Link
                  href={item.link}
                  className={styles.card}
                  draggable={false}
                >
                  <div className={styles.imageWrapper}>
                    <Image
                      src={item.image}
                      alt={item.title}
                      fill
                      sizes="(max-width: 768px) 80vw, 300px"
                      className={styles.image}
                      draggable={false}
                    />
                    <span className={styles.badge}>{item.badge}</span>
                  </div>
                  <div className={styles.textContent}>
                    <h3 className={styles.cardTitle}>{item.title}</h3>
                    <div className={styles.metaFooter}>
                      <span className={styles.cardDate}>{item.date}</span>
                      <MdArrowForward className={styles.readIcon} />
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>

          <div className={styles.carouselControls}>
            <button
              className={styles.controlBtn}
              aria-label="Previous"
              onClick={handleScrollLeft}
            >
              <MdOutlineChevronLeft className={styles.controlIcon} />
            </button>
            <button
              className={styles.controlBtn}
              aria-label="Next"
              onClick={handleScrollRight}
            >
              <MdOutlineChevronRight className={styles.controlIcon} />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
